(function () {
  const state = new Map();

  const selector = 'variant-selects[data-dlx-require-choice="true"]';
  const lineItemSelector = '[data-dlx-line-item-options]';

  function groupKey(group, index) {
    const input = group.querySelector('input[data-option-name], option[data-option-name]');
    const label = group.querySelector('legend, label');
    return input?.dataset.optionName || label?.textContent?.trim() || `option-${index}`;
  }

  function groupsFor(selects) {
    return Array.from(selects.querySelectorAll('fieldset, .product-form__input--dropdown'));
  }

  function getOptionLabel(group) {
    const label = group.querySelector('legend, label');
    if (!label) return '';

    const labelClone = label.cloneNode(true);
    labelClone.querySelectorAll('[data-selected-value]').forEach((selectedValue) => selectedValue.remove());
    return labelClone.textContent?.replace(':', '').trim() || '';
  }

  function getSelectedControl(group) {
    const checkedInput = group.querySelector('input[type="radio"]:checked');
    if (checkedInput) return checkedInput;

    const select = group.querySelector('select');
    if (select && select.value) return select;

    return null;
  }

  function setGroupValue(group, value) {
    if (!value) return;

    const radios = group.querySelectorAll('input[type="radio"]');
    if (radios.length) {
      radios.forEach((input) => {
        input.checked = input.value === value;
      });
      updateSelectedValueLabel(group, value);
      return;
    }

    const select = group.querySelector('select');
    if (select) {
      select.value = value;
      updateSelectedValueLabel(group, value);
    }
  }

  function clearGroupValue(group) {
    group.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = false;
    });

    const select = group.querySelector('select');
    if (select) {
      if (!select.querySelector('option[value=""]')) {
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = select.dataset.dlxPlaceholder || select.getAttribute('aria-label') || 'Choose';
        placeholder.disabled = true;
        select.insertBefore(placeholder, select.firstChild);
      }
      select.value = '';
    }

    updateSelectedValueLabel(group, '');
  }

  function updateSelectedValueLabel(group, value) {
    const selectedValue = group.querySelector('[data-selected-value]');
    if (!selectedValue) return;

    selectedValue.textContent = value || '';
  }

  function availableChoiceValues(group) {
    const radios = Array.from(group.querySelectorAll('input[type="radio"]'));
    if (radios.length) {
      return radios
        .filter((input) => input.value && !input.disabled && !input.classList.contains('disabled'))
        .map((input) => input.value);
    }

    const select = group.querySelector('select');
    if (!select) return [];

    return Array.from(select.options)
      .filter((option) => option.value && !option.disabled)
      .map((option) => option.value);
  }

  function getOnlyChoiceValue(group) {
    const values = availableChoiceValues(group);
    return values.length === 1 ? values[0] : '';
  }

  function hasExplicitVariantSelection(selects) {
    const variantId = new URLSearchParams(window.location.search).get('variant');
    if (!variantId) return false;

    const formVariantId = getProductForm(selects)?.querySelector('input[name="id"]')?.value;
    return !formVariantId || formVariantId === variantId;
  }

  function getProductInfo(selects) {
    return selects.closest('product-info');
  }

  function getProductForm(selects) {
    if (selects.dataset.dlxProductFormId) {
      const explicitForm = document.getElementById(selects.dataset.dlxProductFormId);
      if (explicitForm) return explicitForm;
    }

    const productInfo = getProductInfo(selects);
    const sectionId = selects.dataset.section || productInfo?.dataset.section;
    return productInfo?.querySelector(`#product-form-${sectionId}`) || productInfo?.querySelector('product-form form');
  }

  function getSubmitButton(selects) {
    const productInfo = getProductInfo(selects);
    const sectionId = selects.dataset.section || productInfo?.dataset.section;
    return productInfo?.querySelector(`#ProductSubmitButton-${sectionId}`);
  }

  function getPaymentButton(selects) {
    return getProductInfo(selects)?.querySelector('.shopify-payment-button');
  }

  function getMessage(selects) {
    const productInfo = getProductInfo(selects);
    const sectionId = selects.dataset.section || productInfo?.dataset.section || 'main';
    let message = productInfo?.querySelector(`#DlxVariantRequired-${sectionId}`);

    if (!message && productInfo) {
      message = document.createElement('p');
      message.id = `DlxVariantRequired-${sectionId}`;
      message.className = 'dlx-variant-required-message';
      message.setAttribute('role', 'alert');
      message.hidden = true;

      const buttons = productInfo.querySelector('.product-form__buttons');
      buttons?.insertAdjacentElement('beforebegin', message);
    }

    return message;
  }

  function readState(selects) {
    const sectionId = selects.dataset.section || getProductInfo(selects)?.dataset.section || selects.id || 'main';
    if (!state.has(sectionId)) state.set(sectionId, new Map());
    return state.get(sectionId);
  }

  function selectedValues(selects) {
    const choiceState = readState(selects);
    return groupsFor(selects).map((group, index) => {
      const key = groupKey(group, index);
      return {
        group,
        key,
        label: getOptionLabel(group),
        value: choiceState.get(key) || '',
      };
    });
  }

  function isReady(selects) {
    const values = selectedValues(selects);
    return values.length === 0 || values.every((item) => item.value);
  }

  function missingLabels(selects) {
    return selectedValues(selects)
      .filter((item) => !item.value)
      .map((item) => item.label)
      .filter(Boolean);
  }

  function updateUi(selects, showMessage) {
    const ready = isReady(selects);
    const submit = getSubmitButton(selects);
    const paymentButton = getPaymentButton(selects);
    const message = getMessage(selects);
    const missing = missingLabels(selects);

    groupsFor(selects).forEach((group, index) => {
      const key = groupKey(group, index);
      const chosen = Boolean(readState(selects).get(key));
      group.classList.toggle('dlx-variant-choice-missing', !chosen);
      group.classList.toggle('dlx-variant-choice-made', chosen);
    });

    if (submit) {
      if (!ready) {
        submit.dataset.dlxVariantRequiredDisabled = 'true';
        submit.setAttribute('disabled', 'disabled');
        submit.setAttribute('aria-disabled', 'true');
      } else {
        const wasDisabledByChoiceGuard = submit.dataset.dlxVariantRequiredDisabled === 'true';
        delete submit.dataset.dlxVariantRequiredDisabled;

        if (wasDisabledByChoiceGuard) {
          submit.removeAttribute('disabled');
        }

        if (!submit.disabled) {
          submit.removeAttribute('aria-disabled');
        }
      }
    }

    if (paymentButton) {
      if (!ready) {
        paymentButton.dataset.dlxVariantRequiredHidden = 'true';
        paymentButton.setAttribute('hidden', 'hidden');
        paymentButton.setAttribute('aria-hidden', 'true');
      } else if (!submit?.disabled) {
        delete paymentButton.dataset.dlxVariantRequiredHidden;
        paymentButton.removeAttribute('hidden');
        paymentButton.removeAttribute('aria-hidden');
      }
    }

    if (message) {
      message.textContent = missing.length
        ? selects.dataset.dlxRequiredMessage.replace('[[options]]', missing.join(', '))
        : '';
      message.hidden = ready || !showMessage;
    }
  }

  function prepareChoices(selects) {
    const choiceState = readState(selects);
    const shouldRespectDomChoices = hasExplicitVariantSelection(selects);

    groupsFor(selects).forEach((group, index) => {
      const key = groupKey(group, index);
      const savedValue = choiceState.get(key);
      const selectedControl = getSelectedControl(group);
      const onlyChoiceValue = getOnlyChoiceValue(group);

      if (savedValue) {
        setGroupValue(group, savedValue);
      } else if (shouldRespectDomChoices && selectedControl?.value) {
        choiceState.set(key, selectedControl.value);
        setGroupValue(group, selectedControl.value);
      } else if (onlyChoiceValue) {
        choiceState.set(key, onlyChoiceValue);
        setGroupValue(group, onlyChoiceValue);
      } else {
        clearGroupValue(group);
      }
    });

    updateUi(selects, true);
  }

  function rememberChoice(selects, event) {
    const group = event.target.closest('fieldset, .product-form__input--dropdown');
    if (!group) return;

    const index = groupsFor(selects).indexOf(group);
    const key = groupKey(group, index);
    const control = getSelectedControl(group);
    if (!control?.value) return;

    readState(selects).set(key, control.value);
    updateUi(selects, false);
    scheduleBoot();
  }

  function onSubmit(selects, event) {
    if (isReady(selects)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    updateUi(selects, true);

    const firstMissing = groupsFor(selects).find((group, index) => !readState(selects).get(groupKey(group, index)));
    const focusTarget = firstMissing?.querySelector('input[type="radio"], select');
    focusTarget?.focus({ preventScroll: false });
  }

  function init(selects) {
    if (selects.dataset.dlxRequiredInitialized === 'true') {
      prepareChoices(selects);
      return;
    }

    selects.dataset.dlxRequiredInitialized = 'true';
    prepareChoices(selects);

    selects.addEventListener('change', (event) => rememberChoice(selects, event), true);

    const form = getProductForm(selects);
    form?.addEventListener('submit', (event) => onSubmit(selects, event), true);
  }

  function lineItemGroups(options) {
    return Array.from(options.querySelectorAll('[data-dlx-line-item-option-group]'));
  }

  function getLineItemLabel(group) {
    return group.dataset.dlxOptionLabel || group.querySelector('legend')?.textContent?.replace(':', '').trim() || '';
  }

  function isLineItemReady(options) {
    const groups = lineItemGroups(options);
    return groups.length === 0 || groups.every((group) => group.querySelector('input[type="radio"]:checked'));
  }

  function missingLineItemLabels(options) {
    return lineItemGroups(options)
      .filter((group) => !group.querySelector('input[type="radio"]:checked'))
      .map(getLineItemLabel)
      .filter(Boolean);
  }

  function getLineItemMessage(options) {
    return options.querySelector('[data-dlx-line-item-message]');
  }

  function updateLineItemUi(options, showMessage) {
    const ready = isLineItemReady(options);
    const submit = getSubmitButton(options);
    const paymentButton = getPaymentButton(options);
    const message = getLineItemMessage(options);
    const missing = missingLineItemLabels(options);

    lineItemGroups(options).forEach((group) => {
      const chosen = Boolean(group.querySelector('input[type="radio"]:checked'));
      group.classList.toggle('dlx-variant-choice-missing', !chosen);
      group.classList.toggle('dlx-variant-choice-made', chosen);
    });

    if (submit) {
      if (!ready) {
        submit.dataset.dlxVariantRequiredDisabled = 'true';
        submit.setAttribute('disabled', 'disabled');
        submit.setAttribute('aria-disabled', 'true');
      } else {
        const wasDisabledByChoiceGuard = submit.dataset.dlxVariantRequiredDisabled === 'true';
        delete submit.dataset.dlxVariantRequiredDisabled;

        if (wasDisabledByChoiceGuard) {
          submit.removeAttribute('disabled');
        }

        if (!submit.disabled) {
          submit.removeAttribute('aria-disabled');
        }
      }
    }

    if (paymentButton) {
      if (!ready) {
        paymentButton.dataset.dlxVariantRequiredHidden = 'true';
        paymentButton.setAttribute('hidden', 'hidden');
        paymentButton.setAttribute('aria-hidden', 'true');
      } else if (!submit?.disabled) {
        delete paymentButton.dataset.dlxVariantRequiredHidden;
        paymentButton.removeAttribute('hidden');
        paymentButton.removeAttribute('aria-hidden');
      }
    }

    if (message) {
      message.textContent = missing.length
        ? options.dataset.dlxRequiredMessage.replace('[[options]]', missing.join(', '))
        : '';
      message.hidden = ready || !showMessage;
    }
  }

  function onLineItemSubmit(options, event) {
    if (isLineItemReady(options)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    updateLineItemUi(options, true);

    const firstMissing = lineItemGroups(options).find((group) => !group.querySelector('input[type="radio"]:checked'));
    const focusTarget = firstMissing?.querySelector('input[type="radio"]');
    focusTarget?.focus({ preventScroll: false });
  }

  function initLineItemOptions(options) {
    if (options.dataset.dlxLineItemRequiredInitialized === 'true') {
      updateLineItemUi(options, true);
      return;
    }

    options.dataset.dlxLineItemRequiredInitialized = 'true';
    updateLineItemUi(options, true);

    options.addEventListener('change', () => {
      updateLineItemUi(options, false);
      scheduleBoot();
    }, true);

    const form = getProductForm(options);
    form?.addEventListener('submit', (event) => onLineItemSubmit(options, event), true);
  }

  function boot(root) {
    root.querySelectorAll?.(selector).forEach(init);
    root.querySelectorAll?.(lineItemSelector).forEach(initLineItemOptions);
  }

  function scheduleBoot(root) {
    boot(root || document);
    window.setTimeout(() => boot(document), 0);
    window.setTimeout(() => boot(document), 150);
    window.setTimeout(() => boot(document), 500);
  }

  document.addEventListener('DOMContentLoaded', () => scheduleBoot(document));
  document.addEventListener('product-info:loaded', (event) => scheduleBoot(event.target));
  document.addEventListener('shopify:section:load', (event) => scheduleBoot(event.target));

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) boot(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
